import { useEffect, useState } from "react";
import { IREIGraphDataSet } from "../../../interface/graph";
import { IActual4 } from "../../../interface/myPortfolio";
import { REIProgressSpinner } from "../../../components/ui";
import {
  colors,
  formatNumber,
  formatYAxisTick,
  formatYearAsFY,
  getCurrentFinancialYear,
  makeSplitColorFn,
  segmentColorBySplit,
} from "../../../utils/commonUtil";
import ScrollableGraph from "../../../components/shared/ScrollableGraph";

const EquityChart: React.FC<{
  trackerData: { Actuals: IActual4[]; Forecast: IActual4[] };
  year?: number;
  onYearChangeCallback?: (year: number) => void;
}> = ({ trackerData, year, onYearChangeCallback }) => {
  const currentYear = Number(new Date().getFullYear().toString());
  const [loading, setLoading] = useState<boolean>(false);
  const [currentYearIndex, setCurrentYearIndex] = useState(0);
  const [chartData, setChartData] = useState<IREIGraphDataSet[]>([
    { label: "MarketValue", data: [], backgroundColor: ["rgba(45, 212, 191, 1)"] },
    { label: "LoanBalance", data: [], backgroundColor: ["rgba(156, 163, 175, 1)"] },
  ]);
  const [trakerIndexData, setTrakerIndexData] = useState<{ Actuals: IActual4[]; Forecast: IActual4[] }>();
  const [years, setYears] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(1);
  const [selectedYearForecast, setSelectedYearForecast] = useState<IActual4 | undefined>();
  const displayedYears = 5;
  const isCurrentFinancialYear = selectedYear?.toString()?.slice(2, 4) === getCurrentFinancialYear();

  useEffect(() => {
    if (trackerData) {
      setTrakerIndexData(trackerData);
      setYears(trackerData.Forecast.map((item) => item.Year));
      setChartData((prev) => prev.map((data) => ({ ...data, data: [] })));
      setYears(trackerData.Forecast.map((item) => item.Year));
    }
  }, [trackerData]);

  useEffect(() => {
    setLoading(true);
    getChartData();
    setLoading(false);
  }, [trakerIndexData, currentYearIndex, displayedYears]);

  useEffect(() => {
    if (year && years.length > 0) {
      const idx = years.findIndex((y) => y === year.toString());
      if (idx !== -1 && idx !== currentYearIndex) {
        setCurrentYearIndex(idx);
        setSelectedYear(Number(year));
      }
    }
  }, [year, years]);

  const getChartData = () => {
    if (!trackerData) return;

    const forecastData = trackerData.Forecast;
    const actualLength = trackerData.Actuals.length;

    const marketValueData = forecastData.map((item) => Number(item.MarketValue));
    const loanBalanceData = forecastData.map((item) => Number(item.LoanValue));

    const datasets = [
      {
        label: "Market Value",
        data: marketValueData,
        borderWidth: 3,
        pointRadius: 2,
        tension: 0.4,
        fill: false,
        actualLength,
        segment: segmentColorBySplit(colors.actualMarket, colors.forecastMarket, actualLength),
        pointBackgroundColor: makeSplitColorFn(colors.actualMarket, colors.forecastMarket, actualLength),
        pointBorderColor: makeSplitColorFn(colors.actualMarket, colors.forecastMarket, actualLength),
      },
      {
        label: "Loan Balance",
        data: loanBalanceData,
        borderWidth: 3,
        pointRadius: 2,
        tension: 0.4,
        fill: false,
        actualLength,
        segment: segmentColorBySplit(colors.actualLoan, colors.forecastLoan, actualLength),
        pointBackgroundColor: makeSplitColorFn(colors.actualLoan, colors.forecastLoan, actualLength),
        pointBorderColor: makeSplitColorFn(colors.actualLoan, colors.forecastLoan, actualLength),
      },
    ];

    setChartData(datasets);

    setSelectedYearForecast(
      currentYearIndex <= trackerData.Actuals?.length - 1
        ? trackerData.Actuals[currentYearIndex]
        : trackerData.Forecast[currentYearIndex]
    );
    setSelectedYear(Number(years[currentYearIndex]));
  };

  const customOptions = {
    plugins: {
      legend: {
        display: false,
        position: "top",
      },
    },
    scales: {
      y: {
        ticks: {
          callback: formatYAxisTick,
        },
        grid: {
          display: false,
        },
        beginAtZero: true,
      },
      x: {
        offset: isCurrentFinancialYear,
        ticks: {
          callback: (value: number, index: number) => {
            const actualIndex = currentYearIndex + index;

            if (years.length > 0 && actualIndex < years.length) {
              return formatYearAsFY(years[actualIndex]);
            }
            return value;
          },
        },
        grid: {
          display: false,
        },
      },
    },
  };

  const getClassForValue = (value: string | undefined) => {
    return Number(value) < 0 ? "text-red-500" : "";
  };

  const renderGraphHeader = () => {
    return (
      <div className="flex flex-row justify-around">
        <div className="flex w-1/4 content-end items-center justify-center">
          <b>
            {` FY-${selectedYear?.toString().slice(2, 4)} `}
            {isCurrentFinancialYear
              ? "Actuals YTD"
              : Number(selectedYear?.toString().slice(2, 4)) < Number(getCurrentFinancialYear())
                ? "Actuals"
                : "Forecast"}
          </b>
        </div>
        <div className="flex flex-col text-sm content-center bg-gray-100 border-solid border-gray-200 border-x border-y rounded-md w-3/4">
          <table className="w-full">
            <thead>
              <tr className="flex gap-4 align-bottom justify-between text-center w-full">
                <th className="w-1/4 content-end">Market Value</th>
                <th className="w-1/4 content-end">Loan Balance</th>
                <th className="w-1/4 content-end">Equity</th>
              </tr>
            </thead>
            <tbody>
              <tr className="flex gap-4 justify-between text-center w-full">
                <td className={`w-1/4  ${getClassForValue(selectedYearForecast?.MarketValue)}`}>
                  <b>
                    {Number(selectedYearForecast?.MarketValue) < 0
                      ? `($ ${formatNumber(Math.abs(Number(selectedYearForecast?.MarketValue)))})`
                      : `$ ${formatNumber(Number(selectedYearForecast?.MarketValue))}`}
                  </b>
                </td>
                <td className={`w-1/4 ${getClassForValue(selectedYearForecast?.LoanValue)}`}>
                  <b>
                    {Number(selectedYearForecast?.LoanValue) < 0
                      ? `($ ${formatNumber(Math.abs(Number(selectedYearForecast?.LoanValue)))})`
                      : `$ ${formatNumber(Number(selectedYearForecast?.LoanValue))}`}
                  </b>
                </td>
                <td className={`w-1/4 ${getClassForValue(selectedYearForecast?.Equity)}`}>
                  {selectedYearForecast?.Equity != null && (
                    <b>
                      <span className={getClassForValue(selectedYearForecast?.Equity)}>
                        {Number(selectedYearForecast?.Equity) < 0
                          ? `($ ${formatNumber(Math.ceil(Math.abs(Number(selectedYearForecast?.Equity)) * 10) / 10)})`
                          : `$ ${formatNumber(Math.ceil(Number(selectedYearForecast?.Equity) * 10) / 10)}`}
                      </span>
                    </b>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderGraphLegend = (
    <div className="bg-gray-100 border-solid border-gray-200 border-x border-y rounded-b-md p-3 m-3">
      <div className="flex justify-around">
        <div className="flex gap-2 items-center mr-2">
          <span className="w-4 h-4 bg-red-700"></span>
          <span className="text-left">
            Actual
            <br /> market value
          </span>
        </div>
        <div className="flex gap-2 items-center mr-4">
          <div className="w-4 h-4 bg-blue-800"></div>
          <span className="text-left">
            Actual
            <br /> loan balance
          </span>
        </div>
        <div className="flex gap-2 items-center mr-4">
          <div className="w-4 h-4 bg-slate-500"></div>
          <span className="text-left">
            Forecast
            <br /> market value
          </span>
        </div>
        <div className="flex gap-2 items-center mr-4">
          <div className="w-4 h-4 bg-green-600"></div>
          <span className="text-left">
            Forecast
            <br /> loan balance
          </span>
        </div>
      </div>
    </div>
  );

  const onYearChange = (selectedYearIndex: number) => {
    setCurrentYearIndex(selectedYearIndex);
    setSelectedYear(Number(years[selectedYearIndex]));
    if (onYearChangeCallback) {
      onYearChangeCallback(Number(years[selectedYearIndex].slice(2, 4)));
    }
  };

  return (
    <>
      {loading && (
        <div className="flex items-center">
          <REIProgressSpinner />
        </div>
      )}

      {!loading && (
        <div className="flex flex-col">
          {renderGraphHeader()}
          <div className="mb-4">
            <ScrollableGraph
              id="meqg-graph-equity"
              onYearChange={onYearChange}
              data={chartData}
              displayedYears={displayedYears}
              graphType="line"
              labels={years}
              customOptions={customOptions}
              selectedYearIndex={trakerIndexData?.Actuals.length != 0 ? currentYearIndex : 0}
            />
            {renderGraphLegend}
          </div>
        </div>
      )}
    </>
  );
};

export default EquityChart;
